import { AssistantResponseAggregateChunkSseEvent, AssistantResponseChunkSseEvent, AssistantResponseEndSseEvent, SSE_EVENTS } from "$types/SSE_EVENTS"
import { match } from "ts-pattern"

export type RelatedEvent = (
  AssistantResponseChunkSseEvent
  | AssistantResponseAggregateChunkSseEvent
  | AssistantResponseEndSseEvent
)

export type Params = {
  userMessageId: number
}

export type Context = {
  status: "PENDING" | "SUCCESS" | "ERROR",
  rawContent: string,
  content: string,
  followUpQuestions: string[],
  loadingFollowUpQuestions: boolean
}

type RawContext = {
  status: "PENDING" | "SUCCESS" | "ERROR",
  content: (string | null | undefined)[],
}

export class ChatMessageManager {
  private userMessageId: number
  private rawContext: RawContext = {
    status: "PENDING",
    content: [],
  }
  private chunks: string[] = []

  static delimiterRegex = /^(.*?)<{6}(.*?)$/s
  static partialDelimiterRegex = /^(.*?)<{1,6}$/s

  constructor(
    params: Params
  ) {
    this.userMessageId = params.userMessageId
  }

  addEvent(event: RelatedEvent): void {
    if (event.data.messageId !== this.userMessageId) {
      return
    }

    match(event)
      .with({ type: SSE_EVENTS.ASSISTANT_RESPONSE_CHUNK }, (event) => {
        this.rawContext.content[event.data.index] = event.data.content
      })

      .with({ type: SSE_EVENTS.ASSISTANT_RESPONSE_AGGREGATE_CHUNK }, (event) => {
        for (let i = event.data.fromIndex; i <= event.data.toIndex; i++) {
          this.rawContext.content[i] = null
        }

        this.rawContext.content[event.data.fromIndex] = event.data.content
      })

      .with({ type: SSE_EVENTS.ASSISTANT_RESPONSE_END }, (event) => {
        this.rawContext.status = event.data.status === "SUCCESS" ? "SUCCESS" : "ERROR"
        this.rawContext.content = this.rawContext.content.map((content, index) => {
          if (content === null || content === undefined) {
            return ""
          }
          return content
        })

        if (event.data.error) {
          this.rawContext.status = "ERROR"
          this.rawContext.content = [event.data.error]
        }
      })

      .exhaustive()
  }

  static parseContent = (rawContent: string) => {
    const delimiterMatch = rawContent.match(this.delimiterRegex)

    if (delimiterMatch) {
      return {
        chatResponse: delimiterMatch[1].trim(),
        followUpQuestions: (delimiterMatch[2].trim() || "").split("\n"),
        loadingFollowUpQuestions: true
      }
    } else {
      const partialDelimiterMatch = rawContent.match(this.partialDelimiterRegex)
      if (partialDelimiterMatch) {
        return {
          chatResponse: partialDelimiterMatch[1].trim(),
          followUpQuestions: [],
          loadingFollowUpQuestions: false
        }
      }
    }

    return {
      chatResponse: rawContent.trim(),
      followUpQuestions: [],
      loadingFollowUpQuestions: false
    }
  }

  get context(): Context {
    const rawContent = this.rawContext.content.join("")
    const parsed = ChatMessageManager.parseContent(rawContent)

    const followUpQuestions = parsed.followUpQuestions.filter((question) => question.trim() !== "")

    const context: Context = {
      status: this.rawContext.status,
      rawContent,
      content: parsed.chatResponse,
      followUpQuestions,
      loadingFollowUpQuestions: parsed.loadingFollowUpQuestions
    }

    return context
  }
}
